import React from 'react'
import { GridToolbarSettings } from 'store/grid/gridTypes'
import { BlotterControls, BlotterSearchBar, BlotterToolbarContainer, GroupByWrapper, SwitchStrip } from './toolbarStyles'
import { ButtonStrip, ToggleButton, ToggleButtonStrip, ToolbarButton } from 'view/components/buttons'
import * as gridIcons from '../styles/gridIconSet'
import { Locale, Translation } from 'locale'
import { OrganisationFilterSwitch } from './OrganisationFilterSwitch'
import { crossIcon } from 'view/components/icons/iconSet'
import styled from '@emotion/styled'

export interface DataGridToolbarProps {
  disableFilters?: boolean
  disableGrouping?: boolean
  disableExport?: boolean
  toolbarSettings: GridToolbarSettings
  searchValue: string | null
  renderGroupByToggles: () => JSX.Element | null
  renderCustomToolbarButtons: () => JSX.Element | null
  renderCustomToolbarComponents: () => JSX.Element | null
  renderCustomToolbarSwitches: () => JSX.Element | null
  onSearchUpdated: (value: string) => unknown
  onToolbarSettingsChanged: (toolbarSettings: GridToolbarSettings) => unknown
  onExportCsv: () => unknown
  onRestoreDefaultLayout: () => unknown
  onAutoFitColumns: () => unknown
  onClose?: () => unknown
  showOrgActivityToggle?: boolean
}

export const DataGridToolbarContainer = styled.div`
  display: flex;
  flex-direction: column;
`

export const DataGridToolbar = (props: DataGridToolbarProps) => (
  <BlotterToolbarContainer>
    <DataGridToolbarItems {...props} />
  </BlotterToolbarContainer>
)

export class DataGridToolbarItems extends React.Component<Omit<DataGridToolbarProps, 'displayContent'>> {
  render() {
    const {
      disableFilters,
      disableGrouping,
      disableExport,
      toolbarSettings,
      searchValue,
      renderGroupByToggles,
      renderCustomToolbarButtons,
      renderCustomToolbarComponents,
      renderCustomToolbarSwitches,
      onExportCsv,
      onRestoreDefaultLayout,
      onAutoFitColumns,
      showOrgActivityToggle,
      onClose
    } = this.props

    const groupByToggles = renderGroupByToggles()
    const customToolbarComponents = renderCustomToolbarComponents()

    return (
      <>
        {groupByToggles ? (
          <GroupByWrapper>{groupByToggles}</GroupByWrapper>
        ) : (
          <BlotterSearchBar
            dataTestId={'DataGridToolbar-BlotterSearchBar'}
            value={searchValue}
            onUpdate={this.props.onSearchUpdated}
            placeholder={Locale.translate(Translation.SC_SEARCH_ALL_FIELDS)}
          />
        )}
        <BlotterControls>
          {customToolbarComponents}
          {groupByToggles ? (
            <BlotterSearchBar
              dataTestId={'DataGridToolbar-BlotterSearchBar'}
              value={searchValue}
              onUpdate={this.props.onSearchUpdated}
              placeholder={Locale.translate(Translation.SC_SEARCH_ALL_FIELDS)}
            />
          ) : (
            <GroupByWrapper>{renderGroupByToggles()}</GroupByWrapper>
          )}
          <SwitchStrip>
            {showOrgActivityToggle && <OrganisationFilterSwitch />}
            {renderCustomToolbarSwitches()}
          </SwitchStrip>
          <ButtonStrip spacing="5px">
            <ToolbarButton tooltip={Translation.SC_AUTO_FIT_COLUMNS} onClick={onAutoFitColumns} dataTestId="DataGridToolbar-Button-AutoFit">
              {gridIcons.autoFitIcon}
            </ToolbarButton>
          </ButtonStrip>
          &nbsp;
          <ButtonStrip spacing="5px">
            {!disableExport && (
              <ToolbarButton
                tooltip={Translation.SC_EXPORT_ALL_ROWS}
                onClick={onExportCsv}
                dataTestId="DataGridToolbar-Button-ExportAllRows">
                {gridIcons.exportIcon}
              </ToolbarButton>
            )}
          </ButtonStrip>
          <ToggleButtonStrip>{renderCustomToolbarButtons()}</ToggleButtonStrip>
          <ToggleButtonStrip>
            <ToolbarButton
              tooltip={Translation.SC_RESTORE_DEFAULT_LAYOUT}
              onClick={onRestoreDefaultLayout}
              dataTestId={'DataGridToolbar-RestoreDefaultLayoutButton'}>
              {gridIcons.restoreDefaultIcon}
            </ToolbarButton>
            {!disableGrouping && (
              <ToggleButton
                tooltip={Translation.SC_GROUP_ITEMS}
                onToggle={this.onToggleGrouping}
                on={toolbarSettings.showGrouping}
                dataTestId="DataGridToolbar-Button-ToggleGroupItems">
                {gridIcons.groupRowsIcon}
              </ToggleButton>
            )}
            {!disableFilters && (
              <ToggleButton
                tooltip={Translation.SC_FILTER_ROWS}
                onToggle={this.onToggleFilterControls}
                on={toolbarSettings.showFilter}
                dataTestId="DataGridToolbar-Button-ToggleFilter">
                {gridIcons.filterIcon}
              </ToggleButton>
            )}
            <ToggleButton
              id="ColumnSelectorToggle"
              tooltip={Translation.SC_DISPLAY_HIDE_COLUMNS}
              onToggle={this.onToggleColumnSettings}
              on={toolbarSettings.showColumnSelector}
              dataTestId="DataGridToolbar-ColumnSelectorToggle">
              {gridIcons.cogWheelIcon}
            </ToggleButton>
          </ToggleButtonStrip>
          {onClose && (
            <ToggleButtonStrip>
              <ToolbarButton tooltip={Translation.TC_BUTTON_CLOSE} onClick={onClose}>
                {crossIcon}
              </ToolbarButton>
            </ToggleButtonStrip>
          )}
        </BlotterControls>
      </>
    )
  }

  private onToggleGrouping = () => {
    this.updateToolbarSettings('showGrouping')
  }

  private onToggleColumnSettings = () => {
    this.updateToolbarSettings('showColumnSelector')
  }

  private onToggleFilterControls = () => {
    this.updateToolbarSettings('showFilter')
  }

  private updateToolbarSettings = (propName: keyof GridToolbarSettings) => {
    const { toolbarSettings, onToolbarSettingsChanged } = this.props
    const updatedToolbarSettings: GridToolbarSettings = {
      ...toolbarSettings,
      [propName]: !toolbarSettings[propName]
    }

    onToolbarSettingsChanged(updatedToolbarSettings)
  }
}
